import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import styles from "./Reservation.module.css";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { DateTime } from "luxon";

const ReservationModal = ({ open, handleClose, listing = {} }) => {
  const { userDetails } = useAuth();
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format for datetime-local input
  const formatForInput = (dt) => dt.toFormat("yyyy-LL-dd'T'HH:mm");

  const now = DateTime.now().setZone(userTimeZone);
  const initialStart = listing?.house_visits_from
    ? DateTime.fromISO(listing.house_visits_from, { zone: userTimeZone })
    : now;

  const [selectedStartDate, setSelectedStartDate] = useState(
    formatForInput(initialStart)
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    formatForInput(initialStart.plus({ hours: 2 }))
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Sync start time from listing prop
  useEffect(() => {
    if (listing?.house_visits_from) {
      const start = DateTime.fromISO(listing.house_visits_from, {
        zone: userTimeZone,
      });

      const now = DateTime.now().setZone(userTimeZone);
      const safeStart = start < now ? now : start;

      setSelectedStartDate(formatForInput(safeStart));
      setSelectedEndDate(formatForInput(safeStart.plus({ hours: 2 })));
    }
  }, [listing]);

  // Auto-update end time when start time changes
  useEffect(() => {
    const start = DateTime.fromISO(selectedStartDate, { zone: userTimeZone });
    setSelectedEndDate(formatForInput(start.plus({ hours: 2 })));
  }, [selectedStartDate]);

  const handleSubmit = async () => {
    const start = DateTime.fromISO(selectedStartDate, { zone: userTimeZone });
    const end = DateTime.fromISO(selectedEndDate, { zone: userTimeZone });

    if (!start.isValid || !end.isValid) {
      setError("Please provide valid start and end times.");
      return;
    }

    if (start < now) {
      setError("Start date and time cannot be in the past.");
      return;
    }

    if (end <= start) {
      setError("End time must be after start time.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const payload = {
        listing_id: listing.id,
        booking_start_date: start.toISO(),
        booking_end_date: end.toISO(),
        message,
        creator_user_id: userDetails.id,
        timezone: userTimeZone,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/reservation`,
        payload
      );

      if (response.data.success) {
        toast.success("Reservation created successfully!");
        handleClose();
      } else {
        toast.error("Failed to create the reservation.");
      }
    } catch (err) {
      console.error("Error creating reservation:", err);
      setError(
        err.response?.data?.error ||
          "An error occurred while creating the reservation."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="reservation-modal"
    >
      <Box className={styles.modalContent}>
        <Typography variant="h4" className={styles.header}>
          Schedule a Visit
        </Typography>
        <Typography variant="h6" className={styles.listingTitle}>
          {listing?.title || "Property Title"}
        </Typography>
        <Typography variant="body1" className={styles.listingDetails}>
          {listing?.bedrooms || 0} bed | {listing?.bathrooms || 0} bath |{" "}
          {listing?.area_sqft || 0} sq.ft
        </Typography>
        <Typography variant="body1" className={styles.listingAddress}>
          {listing?.address ||
            `${listing?.city || ""}, ${listing?.state || ""} ${
              listing?.zipcode || ""
            }`}
        </Typography>

        <div className={styles.datePickerContainer}>
          <div className={styles.datePicker}>
            <TextField
              label="Start Date & Time"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={selectedStartDate}
              onChange={(e) => setSelectedStartDate(e.target.value)}
              inputProps={{
                min: formatForInput(now),
              }}
              helperText={
                DateTime.fromISO(selectedStartDate) < now
                  ? "Start cannot be in the past"
                  : ""
              }
              error={DateTime.fromISO(selectedStartDate) < now}
            />
          </div>
          <div className={styles.datePicker}>
            <TextField
              label="End Date & Time"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={selectedEndDate}
              onChange={(e) => setSelectedEndDate(e.target.value)}
              inputProps={{
                min: selectedStartDate,
              }}
              helperText={
                DateTime.fromISO(selectedEndDate) <=
                DateTime.fromISO(selectedStartDate)
                  ? "End must be after start"
                  : ""
              }
              error={
                DateTime.fromISO(selectedEndDate) <=
                DateTime.fromISO(selectedStartDate)
              }
            />
          </div>
        </div>

        {error && (
          <Typography variant="body2" color="error" className={styles.error}>
            {error}
          </Typography>
        )}

        <TextField
          label="Message (optional)"
          multiline
          rows={3}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={styles.messageInput}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          className={styles.submitButton}
        >
          {loading ? "Submitting..." : "Confirm Reservation"}
        </Button>
      </Box>
    </Modal>
  );
};

export default ReservationModal;
